import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { merge, Observable, of, Subject } from "rxjs";
import {
    finalize,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
} from "rxjs/operators";

import { CHECKOUT_ROUTES } from "../../checkout.routes";
import { DataService } from "../../../core/providers/data/data.service";
import { ModalService } from "../../../core/providers/modal/modal.service";
import { StateService } from "../../../core/providers/state/state.service";
import {
    GET_ELIGIBLE_PAYMENT_METHODS,
    ADD_PAYMENT,
    GET_ACTIVE_ORDER,
    TRANSITION_TO_ADDING_ITEMS,
    GET_STRIPE_PUBLISHABLE_KEY,
    GET_STRIPE_SURCHARGE_PERCENT,
    GET_ELIGIBLE_SHIPPING_METHODS,
    SET_SHIPPING_ADDRESS,
    SET_BILLING_ADDRESS,
    SET_SHIPPING_METHOD,
    TRANSITION_TO_ARRANGING_PAYMENT,
} from "./checkout-payment.graphql";
import { NotificationService } from "../../../core/providers/notification/notification.service";
import { GlobalService } from "../../../global.service";

import {
    AddressFragment,
    CreateAddressInput,
    GetActiveOrderQuery,
    AddPaymentMutation,
    AddPaymentMutationVariables,
    TransitionToAddingItemsMutation,
    GetAvailableCountriesQuery,
    GetCustomerAddressesQuery,
    GetEligiblePaymentMethodsQuery,
    GetEligibleShippingMethodsQuery,
    TransitionToArrangingPaymentMutation,
} from "../../../common/generated-types";

import {
    ActiveOrderSnapshot,
    CheckoutPaymentOrderLine,
    CheckoutPaymentSupplierGroup,
    GetStripePublishableKeyQuery,
    GetStripeSurchargePercentQuery,
    SetOrderBillingAddressMutationResult,
} from "../../checkout.types";

import {
    GET_AVAILABLE_COUNTRIES,
    GET_CUSTOMER_ADDRESSES,
} from "../../../common/graphql/documents.graphql";

import { AddressModalComponent } from "../../../shared/components/address-modal/address-modal.component";
import { CheckoutPaymentStripeService } from "../../providers/checkout-payment-stripe.service";
import * as utils from "./checkout-payment.utils";

import {
    ORDER_STATE,
    GRAPHQL_TYPENAME,
    REDIRECT_STATUS,
    PAYMENT_ERROR_MESSAGES,
} from "../../../shared/constants/order.constants";
import {
    APOLLO_FETCH_POLICY,
    CHECKOUT_PAYMENT_ERRORS,
    CHECKOUT_PAYMENT_UI,
    POST_PAYMENT_ACTIVE_ORDER_CLEAR_DELAY_MS,
    STATE_SERVICE_KEY,
    STRIPE_PAYMENT_ELEMENT_DOM_ID,
    checkoutConfirmationRelativePath,
} from "./checkout-payment.constants";

@Component({
    selector: "vsf-checkout-payment",
    templateUrl: "./checkout-payment.component.html",
    styleUrls: ["./checkout-payment.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutPaymentComponent implements OnInit, OnDestroy {
    selectedPaymentMethod?: string;
    cardNumber?: string;
    expMonth?: number;
    expYear?: number;

    paymentMethods$!: Observable<
        GetEligiblePaymentMethodsQuery["eligiblePaymentMethods"]
    >;
    customerAddresses$!: Observable<AddressFragment[]>;
    signedIn$!: Observable<boolean>;
    availableCountries$!: Observable<
        GetAvailableCountriesQuery["availableCountries"]
    >;
    eligibleShippingMethods$!: Observable<
        GetEligibleShippingMethodsQuery["eligibleShippingMethods"]
    >;

    activeOrderCode?: string;
    activeOrder?: ActiveOrderSnapshot;
    termsAccepted = false;
    venduroTermsAccepted = false;
    paymentErrorMessage?: string;
    editingAddress = false;
    stripeSurchargePercent = 0;
    shippingMethodId?: string;
    supplierGroups: CheckoutPaymentSupplierGroup[] = [];
    isProcessingPayment = false;
    private billingFollowsShipping = true;

    private readonly reloadCustomerAddresses$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private router: Router,
        private route: ActivatedRoute,
        private notificationService: NotificationService,
        private cdr: ChangeDetectorRef,
        private globalService: GlobalService,
        private modalService: ModalService,
        public stripeService: CheckoutPaymentStripeService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stripeService.destroy();
    }

    async ngOnInit(): Promise<void> {
        this.paymentMethods$ = this.dataService
            .query<GetEligiblePaymentMethodsQuery>(GET_ELIGIBLE_PAYMENT_METHODS)
            .pipe(map((res) => res.eligiblePaymentMethods));

        this.signedIn$ = this.stateService.select((state) => state.signedIn);

        this.customerAddresses$ = merge(of(undefined), this.reloadCustomerAddresses$).pipe(
            switchMap(() =>
                this.dataService.query<GetCustomerAddressesQuery>(
                    GET_CUSTOMER_ADDRESSES,
                    {},
                    APOLLO_FETCH_POLICY.NETWORK_ONLY
                )
            ),
            map((data) =>
                data.activeCustomer ? data.activeCustomer.addresses || [] : []
            ),
            shareReplay(1)
        );

        this.availableCountries$ = this.dataService
            .query<GetAvailableCountriesQuery>(GET_AVAILABLE_COUNTRIES)
            .pipe(map((data) => data.availableCountries));

        this.paymentMethods$
            .pipe(takeUntil(this.destroy$))
            .subscribe((methods) => {
                const stripeMethod = methods?.find(
                    (method) =>
                        method.isEligible &&
                        this.stripeService.isStripePaymentMethod(method.code)
                );

                if (!stripeMethod) {
                    return;
                }

                this.dataService
                    .query<GetStripePublishableKeyQuery>(GET_STRIPE_PUBLISHABLE_KEY)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(({ stripePublishableKey }) => {
                        if (!stripePublishableKey) {
                            this.paymentErrorMessage =
                                PAYMENT_ERROR_MESSAGES.STRIPE_PUBLISHABLE_KEY_NOT_CONFIGURED;
                            this.cdr.detectChanges();
                            return;
                        }

                        this.paymentErrorMessage = undefined;
                        this.stripeService.initializeStripe(
                            stripePublishableKey,
                            () => this.cdr.detectChanges(),
                            (msg) => {
                                this.paymentErrorMessage = msg;
                                this.cdr.detectChanges();
                            }
                        );
                    });
            });

        this.dataService
            .query<GetActiveOrderQuery>(GET_ACTIVE_ORDER)
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ activeOrder }) => {
                if (!activeOrder || activeOrder.__typename !== GRAPHQL_TYPENAME.ORDER) {
                    return;
                }

                this.activeOrderCode = activeOrder.code;
                this.activeOrder = activeOrder as ActiveOrderSnapshot;
                this.groupLinesBySupplier();

                this.shippingMethodId =
                    activeOrder.shippingLines?.[0]?.shippingMethod?.id;

                if (this.shippingMethodId) {
                    this.globalService.setShippingId(this.shippingMethodId);
                }

                this.ensureProfileAddressesOnOrder(this.activeOrder);
                this.cdr.detectChanges();
            });

        this.eligibleShippingMethods$ = this.dataService
            .query<GetEligibleShippingMethodsQuery>(GET_ELIGIBLE_SHIPPING_METHODS)
            .pipe(map((data) => data.eligibleShippingMethods));

        this.eligibleShippingMethods$
            .pipe(takeUntil(this.destroy$))
            .subscribe((methods) => {
                if (methods?.length > 0 && !this.shippingMethodId) {
                    this.setShippingMethod(methods[0].id);
                }
            });

        this.dataService
            .query<GetStripeSurchargePercentQuery>(GET_STRIPE_SURCHARGE_PERCENT)
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ stripeSurchargePercent }) => {
                this.stripeSurchargePercent = stripeSurchargePercent || 0;
                this.globalService.setStripeSurchargePercent(
                    stripeSurchargePercent || 0
                );
            });

        this.globalService.proceedToPayment$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.selectedPaymentMethod) {
                    this.completeOrder(this.selectedPaymentMethod);
                } else {
                    this.notificationService
                        .error(CHECKOUT_PAYMENT_UI.SELECT_PAYMENT_METHOD_REQUIRED)
                        .subscribe();
                }
            });
    }

    onSelectSavedShippingAddress(
        addressId: string,
        addresses: AddressFragment[]
    ): void {
        if (!addressId) {
            return;
        }

        const address = addresses.find((a) => a.id === addressId);
        if (address) {
            this.applyShippingAddressFromSaved(address);
        }
    }

    openAddShippingAddress(): void {
        this.modalService
            .fromComponent<AddressFragment, AddressModalComponent>(
                AddressModalComponent,
                {
                    locals: {
                        title: CHECKOUT_PAYMENT_UI.ADD_SHIPPING_ADDRESS_MODAL_TITLE,
                        hideCompany: true,
                    },
                    closable: true,
                }
            )
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((created: AddressFragment | undefined) => {
                if (created) {
                    this.applyShippingAddressFromSaved(created);
                }
            });
    }

    onSelectSavedBillingAddress(
        addressId: string,
        addresses: AddressFragment[]
    ): void {
        if (!addressId) {
            return;
        }

        const address = addresses.find((a) => a.id === addressId);
        if (address) {
            this.billingFollowsShipping = false;
            this.setBillingAddress(utils.valueToAddressInput(address));
        }
    }

    openAddBillingAddress(): void {
        this.modalService
            .fromComponent<AddressFragment, AddressModalComponent>(
                AddressModalComponent,
                {
                    locals: {
                        title: CHECKOUT_PAYMENT_UI.ADD_BILLING_ADDRESS_MODAL_TITLE,
                        hideCompany: false,
                    },
                    closable: true,
                }
            )
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((created: AddressFragment | undefined) => {
                if (created) {
                    this.billingFollowsShipping = false;
                    this.reloadCustomerAddresses$.next();
                    this.setBillingAddress(utils.valueToAddressInput(created));
                }
            });
    }

    onUseShippingAsBilling(): void {
        this.billingFollowsShipping = true;
        const ship = this.activeOrder?.shippingAddress;
        if (!ship?.streetLine1 || !ship?.city) {
            return;
        }
        this.setBillingAddress(utils.orderAddressToCreateInput(ship));
    }

    saveGuestBillingAddress(formValue: Partial<CreateAddressInput>): void {
        this.billingFollowsShipping = false;
        this.setBillingAddress(utils.valueToAddressInput(formValue));
    }

    get shippingAddressMissingPhone(): boolean {
        const phone = this.activeOrder?.shippingAddress?.phoneNumber;
        return (
            !!this.activeOrder?.shippingAddress?.streetLine1 &&
            !(phone && String(phone).trim())
        );
    }

    saveGuestShippingAddress(formValue: Partial<CreateAddressInput>): void {
        this.applyShippingAddressInput(utils.valueToAddressInput(formValue));
    }

    private ensureProfileAddressesOnOrder(order: ActiveOrderSnapshot): void {
        this.signedIn$.pipe(take(1), takeUntil(this.destroy$)).subscribe(
            (signedIn) => {
                if (!signedIn) {
                    return;
                }

                this.customerAddresses$
                    .pipe(take(1), takeUntil(this.destroy$))
                    .subscribe((addresses) => {
                        if (!addresses?.length) {
                            return;
                        }

                        this.tryApplyDefaultBillingToOrder(order, addresses);

                        const pickShip =
                            addresses.find((a) => a.defaultShippingAddress) ||
                            addresses[0];

                        if (!pickShip) {
                            return;
                        }

                        const shipAddr = order.shippingAddress;
                        const hasShip = !!shipAddr?.streetLine1;
                        const matchedAddress =
                            hasShip && !!shipAddr
                                ? addresses.find((a) =>
                                      utils.orderAddressMatchesSaved(shipAddr, a)
                                  )
                                : undefined;

                        if (!hasShip || !matchedAddress) {
                            this.applyShippingAddressFromSaved(pickShip);
                        } else if (
                            matchedAddress.phoneNumber &&
                            !shipAddr?.phoneNumber?.trim()
                        ) {
                            this.applyShippingAddressFromSaved(matchedAddress);
                        }
                    });
            }
        );
    }

    private tryApplyDefaultBillingToOrder(
        order: ActiveOrderSnapshot,
        addresses: AddressFragment[]
    ): void {
        const bill = order?.billingAddress;
        if (bill && (bill.streetLine1 || bill.city)) {
            return;
        }

        const billAddr = addresses.find((a) => a.defaultBillingAddress);
        if (!billAddr || !(billAddr.streetLine1 || billAddr.city)) {
            return;
        }

        this.setBillingAddress(utils.valueToAddressInput(billAddr));
    }

    private applyShippingAddressFromSaved(address: AddressFragment): void {
        this.applyShippingAddressInput(utils.valueToAddressInput(address));
    }

    private applyShippingAddressInput(input: CreateAddressInput): void {
        if (!input.streetLine1 || !input.city) {
            return;
        }

        this.dataService
            .mutate(SET_SHIPPING_ADDRESS, { input })
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.reloadCustomerAddresses$.next();

                if (this.billingFollowsShipping) {
                    this.setBillingAddress(input);
                }

                this.dataService
                    .query<GetActiveOrderQuery>(
                        GET_ACTIVE_ORDER,
                        {},
                        APOLLO_FETCH_POLICY.NETWORK_ONLY
                    )
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((r) => {
                        if (r.activeOrder) {
                            this.activeOrder = r.activeOrder as ActiveOrderSnapshot;
                            this.billingFollowsShipping =
                                utils.isBillingSameAsShipping(this.activeOrder) ||
                                this.billingFollowsShipping;
                            this.groupLinesBySupplier();
                        }
                        this.cdr.markForCheck();
                    });

                this.dataService
                    .query<GetEligibleShippingMethodsQuery>(
                        GET_ELIGIBLE_SHIPPING_METHODS
                    )
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((methodsRes) => {
                        const methods = methodsRes.eligibleShippingMethods;
                        if (methods && methods.length > 0) {
                            this.setShippingMethod(methods[0].id);
                        } else {
                            this.cdr.markForCheck();
                        }
                    });
            });
    }

    async completeOrder(paymentMethodCode: string) {
        if (this.isProcessingPayment) {
            return;
        }

        this.isProcessingPayment = true;
        this.cdr.detectChanges();

        try {
            if (this.activeOrder?.state !== ORDER_STATE.ARRANGING_PAYMENT) {
                await this.dataService
                    .mutate<TransitionToArrangingPaymentMutation>(
                        TRANSITION_TO_ARRANGING_PAYMENT
                    )
                    .toPromise();
            }

            if (this.stripeService.isStripePaymentMethod(paymentMethodCode)) {
                await this.handleStripePayment();
            } else {
                this.handleOtherPaymentMethods(paymentMethodCode);
            }
        } catch {
            this.notificationService
                .error(PAYMENT_ERROR_MESSAGES.UNABLE_TO_PROCEED_TO_PAYMENT)
                .subscribe();

            this.isProcessingPayment = false;
            this.cdr.detectChanges();
        }
    }
    private async handleStripePayment(): Promise<void> {
        try {
            if (!this.activeOrderCode) {
                throw new Error(CHECKOUT_PAYMENT_ERRORS.ACTIVE_ORDER_CODE_MISSING);
            }

            const result = await this.stripeService.handleStripePayment(this.activeOrderCode);

            if (result?.error) {
                console.error("Stripe error:", result.error);
                this.isProcessingPayment = false;
                this.cdr.detectChanges();
                this.notificationService
                    .error(result.error.message || PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                    .subscribe();
                return;
            }

            const status = result?.paymentIntent?.status;

            if (status === REDIRECT_STATUS.SUCCEEDED) {
                this.router.navigate(
                    [checkoutConfirmationRelativePath(this.activeOrderCode)],
                    {
                        relativeTo: this.route,
                        queryParams: {
                            redirect_status: REDIRECT_STATUS.SUCCEEDED,
                        },
                    }
                );
                return;
            }

            this.isProcessingPayment = false;
            this.cdr.detectChanges();
            this.notificationService
                .error(PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                .subscribe();
        } catch (error) {
            console.error("Stripe payment error:", error);
            this.isProcessingPayment = false;
            this.cdr.detectChanges();
            this.notificationService
                .error(PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                .subscribe();
        }
    }

    retryStripeInitialization(): void {
        this.stripeService.destroyPaymentElement();
        this.paymentErrorMessage = undefined;
        this.cdr.detectChanges();
    }

    cancelPayment(): void {
        this.dataService
            .mutate<TransitionToAddingItemsMutation>(
                TRANSITION_TO_ADDING_ITEMS
            )
            .pipe(
                finalize(() =>
                    this.router.navigate([`/${CHECKOUT_ROUTES.CART}`])
                )
            )
            .subscribe();
    }

    private handleOtherPaymentMethods(paymentMethodCode: string): void {
        this.dataService
            .mutate<AddPaymentMutation, AddPaymentMutationVariables>(
                ADD_PAYMENT,
                {
                    input: {
                        method: paymentMethodCode,
                        metadata: {},
                    },
                }
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async ({ addPaymentToOrder }) => {
                    switch (addPaymentToOrder?.__typename) {
                        case GRAPHQL_TYPENAME.ORDER: {
                            const order = addPaymentToOrder;

                            if (
                                order.state === ORDER_STATE.PAYMENT_SETTLED ||
                                order.state === ORDER_STATE.PAYMENT_AUTHORIZED
                            ) {
                                await new Promise<void>((resolve) =>
                                    setTimeout(() => {
                                        this.stateService.setState(
                                            STATE_SERVICE_KEY.ACTIVE_ORDER_ID,
                                            null
                                        );
                                        resolve();
                                    }, POST_PAYMENT_ACTIVE_ORDER_CLEAR_DELAY_MS)
                                );

                                this.router.navigate(
                                    [checkoutConfirmationRelativePath(order.code)],
                                    {
                                        relativeTo: this.route,
                                        queryParams: {
                                            redirect_status:
                                                REDIRECT_STATUS.BANK_TRANSFER,
                                            payment_method: paymentMethodCode,
                                        },
                                    }
                                );
                            } else {
                                this.isProcessingPayment = false;
                                this.cdr.detectChanges();
                            }
                            break;
                        }

                        case GRAPHQL_TYPENAME.ORDER_PAYMENT_STATE_ERROR:
                        case GRAPHQL_TYPENAME.PAYMENT_DECLINED_ERROR:
                        case GRAPHQL_TYPENAME.PAYMENT_FAILED_ERROR:
                        case GRAPHQL_TYPENAME.ORDER_STATE_TRANSITION_ERROR:
                            this.isProcessingPayment = false;
                            this.cdr.detectChanges();
                            this.notificationService
                                .error(PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                                .subscribe();
                            break;

                        default:
                            this.isProcessingPayment = false;
                            this.cdr.detectChanges();
                            this.notificationService
                                .error(PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                                .subscribe();
                            break;
                    }
                },
                error: (error) => {
                    console.error("Payment error:", error);
                    this.isProcessingPayment = false;
                    this.cdr.detectChanges();
                    this.notificationService
                        .error(PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED)
                        .subscribe();
                },
            });
    }

    selectPaymentMethod(methodCode: string): void {
        this.selectedPaymentMethod = methodCode;
        this.globalService.setSelectedPaymentMethod(methodCode);
        this.termsAccepted = false;
        this.cdr.detectChanges();

        const isStripe = this.stripeService.isStripePaymentMethod(methodCode);
        this.globalService.setIsStripeSelected(isStripe);

        if (!isStripe) {
            return;
        }

        if (this.stripeService.clientSecret && this.stripeService.elements) {
            setTimeout(() => {
                this.stripeService.mountPaymentElement(
                    STRIPE_PAYMENT_ELEMENT_DOM_ID,
                    () => this.cdr.detectChanges()
                );
            }, 0);
            return;
        }

        this.stripeService.createNewPaymentIntent(() => {
            setTimeout(() => {
                this.stripeService.mountPaymentElement(
                    STRIPE_PAYMENT_ELEMENT_DOM_ID,
                    () => this.cdr.detectChanges()
                );
            }, 0);
        });
    }

    clearErrorAndResetPayment(): void {
        this.selectedPaymentMethod = undefined;
        this.globalService.setSelectedPaymentMethod(null);
        this.termsAccepted = false;
        this.paymentErrorMessage = undefined;
        this.isProcessingPayment = false;
        this.stripeService.destroyPaymentElement();
        this.cdr.detectChanges();
    }

    setBillingAddress(input: CreateAddressInput): void {
        this.dataService
            .mutate<SetOrderBillingAddressMutationResult>(SET_BILLING_ADDRESS, {
                input,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                const next = res.setOrderBillingAddress;
                if (next.__typename === GRAPHQL_TYPENAME.ORDER) {
                    this.activeOrder = next as ActiveOrderSnapshot;
                    this.cdr.markForCheck();
                }
            });
    }

    setShippingMethod(id: string): void {
        this.shippingMethodId = id;
        this.globalService.setShippingId(id);

        this.dataService
            .mutate(
                SET_SHIPPING_METHOD,
                { shippingMethodId: id }
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.detectChanges());
    }

    getAvailableStock(line: CheckoutPaymentOrderLine): number {
        return utils.getAvailableStock(line);
    }

    groupLinesBySupplier(): void {
        this.supplierGroups = utils.groupLinesBySupplier(
            this.activeOrder?.lines
        );
    }
}
